/**
 * Feedback Widget Provider
 * Wrapper to inject the feedback widget throughout the entire app
 * Place at the root level of your app (e.g., in App.tsx)
 */

import React, { createContext, useContext } from 'react';
import FeedbackWidget from '@/components/feedback/FeedbackWidget';
import {
  FeedbackWidgetConfig,
  DEFAULT_FEEDBACK_CONFIG,
  getEnvironmentConfig,
} from '@/lib/feedback-widget-config';

interface FeedbackWidgetContextType {
  config: FeedbackWidgetConfig;
}

const FeedbackWidgetContext = createContext<FeedbackWidgetContextType | undefined>(undefined);

export function useFeedbackWidgetContext() {
  const context = useContext(FeedbackWidgetContext);
  if (!context) {
    throw new Error(
      'useFeedbackWidgetContext must be used within FeedbackWidgetProvider'
    );
  }
  return context;
}

interface FeedbackWidgetProviderProps {
  children: React.ReactNode;
  config?: Partial<FeedbackWidgetConfig>;
}

export function FeedbackWidgetProvider({
  children,
  config,
}: FeedbackWidgetProviderProps) {
  // Merge configs: defaults → environment → user
  const mergedConfig: FeedbackWidgetConfig = {
    ...DEFAULT_FEEDBACK_CONFIG,
    ...getEnvironmentConfig(),
    ...config,
  };

  // Debug logging
  if (mergedConfig.debug) {
    console.log('[FeedbackWidget] Initialized with config:', mergedConfig);
  }

  // Check if we should show the widget on the current path
  const shouldShowWidget = () => {
    if (!mergedConfig.enabled) {
      if (mergedConfig.debug) console.log('[FeedbackWidget] Disabled globally');
      return false;
    }

    const currentPath = window.location.pathname;
    const isHidden = mergedConfig.hideOnPaths.some(path => currentPath.startsWith(path));

    if (isHidden && mergedConfig.debug) {
      console.log(`[FeedbackWidget] Hidden on path: ${currentPath}`);
    }

    return !isHidden;
  };

  return (
    <FeedbackWidgetContext.Provider value={{ config: mergedConfig }}>
      {children}
      {shouldShowWidget() && (
        <FeedbackWidget
          position={mergedConfig.position}
          theme={mergedConfig.theme}
        />
      )}
    </FeedbackWidgetContext.Provider>
  );
}

export default FeedbackWidgetProvider;
